import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Logo } from '../assets/logo.svg';
import CarPaintSimulator from '../components/CarPaintSimulator';
import API_URL from '../api/baseURL';

function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); io.disconnect(); }
    }, { threshold });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, vis];
}

const AC = '#B4FF3A'; // acid lime accent
const BG = '#070707';
const SU = '#111111'; // surface

const Home = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stats, setStats] = useState({ carOwners: null, workshops: null, vehicles: null });

  // Live signup counts for the hero (aggregate only, cached server-side).
  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/public/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (alive && d) setStats({ carOwners: d.carOwners ?? 0, workshops: d.workshops ?? 0, vehicles: d.vehicles ?? 0 }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Show the live number once loaded; a quiet placeholder while fetching.
  const fmtCount = (n) => (n === null ? '—' : Number(n).toLocaleString('fr-FR'));

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (_) {} };
  }, []);

  const scroll = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMobileOpen(false); };

  const [rP, vP] = useReveal();
  const [rS, vS] = useReveal();
  const [rF1, vF1] = useReveal();
  const [rF2, vF2] = useReveal();
  const [rF3, vF3] = useReveal();
  const [rF4, vF4] = useReveal();
  const [rE, vE] = useReveal();
  const [rA, vA] = useReveal();

  const featureRefs = [[rF1, vF1], [rF2, vF2], [rF3, vF3], [rF4, vF4]];

  const PAIN = [
    { stat: '×3', label: "L'écart de prix", desc: "Pour la même vidange, le tarif varie du simple au triple entre les ateliers — sans aucune explication ni justification." },
    { stat: '0', label: "Fiche d'entretien", desc: "Historique d'entretien conservé après votre passage en garage. En papier, ça se perd. Numériquement, ça n'existait pas." },
    { stat: '6/10', label: "Ne savent pas", desc: "Automobilistes qui ignorent si leur assurance ou contrôle technique est encore valide. Résultat : amendes, refus d'indemnisation." },
  ];

  const FEATURES = [
    {
      tag: '01 — TRANSPARENCE',
      id: 'solution',
      title: 'Un score de confiance\npour votre flotte.',
      body: "Chaque véhicule de votre flotte est noté en temps réel sur 5 critères : assurance, contrôle technique, vidange, réactivité et santé globale. Un chiffre. Une vérité. Aucune mauvaise surprise.",
      bullets: ['Score live sur 100 avec badge de niveau', 'Alertes 30 jours avant toute expiration', 'Mise à jour du kilométrage depuis le dashboard', 'Drill-down par véhicule en un clic'],
      visual: 'score',
    },
    {
      tag: '02 — CONTRÔLE',
      id: null,
      title: 'Devis. Facture.\nPaiement Wave.',
      body: "Faites une demande de service. Les ateliers partenaires vous envoient leurs devis. Vous acceptez le meilleur. La facture arrive détaillée ligne par ligne. Vous payez en Wave depuis votre téléphone. L'atelier reçoit son argent automatiquement.",
      bullets: ['Demandes de service en ligne avec GPS', 'Devis comparatifs des ateliers partenaires', 'Factures avec TVA, marques, références pièces', 'Paiement Wave — zéro cash, zéro contact'],
      visual: 'flow',
    },
    {
      tag: "03 — INTELLIGENCE",
      id: null,
      title: "L'IA qui connaît\nvotre voiture.",
      body: "1PARC analyse l'historique d'entretien de votre véhicule et prédit les pannes avant qu'elles arrivent. Décrivez un symptôme en français — on vous dit ce que c'est, l'urgence, et le coût estimé.",
      bullets: ['Prédiction des pannes à venir avec délai', 'Diagnostic par symptôme en langage naturel', 'Suggestions de pièces basées sur 1000+ interventions similaires', 'Score de santé par véhicule avec recommandations'],
      visual: 'ai',
    },
    {
      tag: '04 — ATELIERS',
      id: 'ateliers',
      title: 'Pour les ateliers :\nun business moderne.',
      body: "Recevez des demandes en temps réel, envoyez des devis professionnels, partagez vos documents sur WhatsApp, encaissez directement sur Wave. Sans toucher à la caisse. Sans courir après les paiements.",
      bullets: ['Tableau de bord avec KPIs en temps réel', 'Création de devis et factures professionnels', 'Partage WhatsApp en 2 clics', 'Virement Wave automatique à chaque paiement client'],
      visual: 'workshop',
    },
  ];

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", background: BG, color: '#F2F2F2', overflowX: 'hidden' }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .bc { font-family: 'Barlow Condensed', sans-serif; }
        .mn { font-family: 'Courier New', monospace; }
        .rv { opacity:0; transform:translateY(22px); transition:opacity 0.8s cubic-bezier(.16,1,.3,1),transform 0.8s cubic-bezier(.16,1,.3,1); }
        .rv.on { opacity:1; transform:translateY(0); }
        @keyframes bob { 0%,100%{transform:translateY(0);opacity:.45} 50%{transform:translateY(7px);opacity:1} }
        .scroll-cue { animation:bob 1.8s ease-in-out infinite; }
        .d1{transition-delay:.08s}.d2{transition-delay:.17s}.d3{transition-delay:.26s}.d4{transition-delay:.35s}
        .ba { background:${AC}; color:#000; border:none; cursor:pointer; font-family:'Manrope',sans-serif; font-weight:700; transition:all .22s; }
        .ba:hover { background:#ccff44; transform:translateY(-1px); }
        .bo { background:transparent; border:1px solid rgba(255,255,255,.13); color:#F2F2F2; cursor:pointer; font-family:'Manrope',sans-serif; font-weight:600; transition:all .22s; }
        .bo:hover { border-color:rgba(255,255,255,.35); background:rgba(255,255,255,.04); transform:translateY(-1px); }
        .bd { background:#050505; color:${AC}; border:1px solid rgba(0,0,0,.2); cursor:pointer; font-family:'Manrope',sans-serif; font-weight:700; transition:all .22s; }
        .bd:hover { background:#111; transform:translateY(-1px); }
        .nl { background:none; border:none; color:#666; cursor:pointer; font-family:'Manrope',sans-serif; font-size:13px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; transition:color .2s; padding:0; }
        .nl:hover { color:#F2F2F2; }
        .hc { transition:border-color .25s,background .25s; }
        .hc:hover { border-color:rgba(180,255,58,.2) !important; background:rgba(180,255,58,.02) !important; }
        .sb-t { height:3px; background:rgba(255,255,255,.06); border-radius:2px; overflow:hidden; }
        .sb-f { height:100%; border-radius:2px; background:${AC}; }
        .grain-overlay { position:fixed; inset:0; z-index:9999; pointer-events:none; opacity:.022;
          background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          background-size:200px; }
        @media(max-width:900px){
          .hw{grid-template-columns:1fr !important;}
          .hp{display:none !important;}
          .fg{grid-template-columns:1fr !important;}
          .fv{display:none !important;}
          .eg{grid-template-columns:repeat(2,1fr) !important;}
          .pg{grid-template-columns:1fr !important;}
          .ftg{grid-template-columns:1fr !important; gap:28px !important;}
          .hm{display:none !important;}
          .sm{display:flex !important;}
          .hh{font-size:clamp(60px,14vw,96px) !important; letter-spacing:-2px !important;}
          .sol-grid{grid-template-columns:1fr !important;}
          .cta-wrap{flex-direction:column !important;}
        }
        @media(min-width:901px){.sm{display:none !important;}}
      `}</style>

      {/* Grain */}
      <div className="grain-overlay" />

      {/* ── NAV ─────────────────────────────────────────────── */}
      <nav style={{
        position:'fixed',top:0,left:0,right:0,zIndex:200,
        padding:scrolled?'13px 0':'23px 0',
        background:scrolled?'rgba(7,7,7,.93)':'transparent',
        backdropFilter:scrolled?'blur(18px)':'none',
        borderBottom:scrolled?'1px solid rgba(255,255,255,.055)':'none',
        transition:'all .4s ease',
      }}>
        <div style={{maxWidth:1280,margin:'0 auto',padding:'0 32px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}} onClick={()=>navigate('/')}>
            <Logo style={{height:26}}/>
            <span className="bc" style={{fontSize:20,fontWeight:900,color:'#F2F2F2',letterSpacing:'1px',textTransform:'uppercase'}}>1PARC</span>
          </div>
          <div className="hm" style={{display:'flex',alignItems:'center',gap:32}}>
            {[['solution','La Solution'],['ateliers','Ateliers'],['ecosysteme',"L'Écosystème"],['histoire','Notre Histoire']].map(([id,lbl])=>(
              <button key={id} className="nl" onClick={()=>scroll(id)}>{lbl}</button>
            ))}
            <div style={{display:'flex',gap:10,marginLeft:8}}>
              <button className="bo" style={{padding:'9px 20px',borderRadius:8,fontSize:13}} onClick={()=>navigate('/login')}>Connexion</button>
              <button className="ba" style={{padding:'9px 20px',borderRadius:8,fontSize:13}} onClick={()=>navigate('/signup')}>Commencer →</button>
            </div>
          </div>
          <button className="sm" onClick={()=>setMobileOpen(!mobileOpen)}
            style={{background:'none',border:'none',color:'#F2F2F2',cursor:'pointer',display:'none',padding:8}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              {mobileOpen?<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>:<><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen&&(
        <div style={{position:'fixed',inset:0,zIndex:190,background:BG,paddingTop:96,display:'flex',flexDirection:'column',alignItems:'center',gap:28}}>
          {[['solution','La Solution'],['ateliers','Ateliers'],['ecosysteme',"L'Écosystème"],['histoire','Notre Histoire']].map(([id,lbl])=>(
            <button key={id} onClick={()=>scroll(id)} className="bc"
              style={{background:'none',border:'none',color:'#F2F2F2',cursor:'pointer',fontSize:40,fontWeight:900,textTransform:'uppercase'}}>{lbl}</button>
          ))}
          <div style={{width:40,height:1,background:'rgba(255,255,255,.1)'}}/>
          <button className="bo" style={{width:260,padding:16,borderRadius:10,fontSize:15}} onClick={()=>navigate('/login')}>Connexion</button>
          <button className="ba" style={{width:260,padding:16,borderRadius:10,fontSize:15}} onClick={()=>navigate('/signup')}>Commencer →</button>
        </div>
      )}

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section style={{minHeight:'100vh',display:'flex',flexDirection:'column',justifyContent:'center',paddingTop:80,borderBottom:'1px solid rgba(255,255,255,.07)',position:'relative'}}>
        <div style={{maxWidth:1280,margin:'0 auto',padding:'80px 32px 60px',width:'100%'}}>
          <div className="hw" style={{display:'grid',gridTemplateColumns:'1fr 480px',gap:72,alignItems:'center'}}>

            {/* Left — Copy */}
            <div>
              <div style={{display:'inline-flex',alignItems:'center',gap:8,marginBottom:32}}>
                <div style={{width:7,height:7,borderRadius:'50%',background:AC,boxShadow:`0 0 8px ${AC}`}}/>
                <span style={{fontSize:11,fontWeight:700,color:AC,letterSpacing:'.14em',textTransform:'uppercase'}}>
                  Gestion de flotte automobile — Côte d'Ivoire
                </span>
              </div>

              <h1 className="bc hh" style={{fontSize:'clamp(68px,8.5vw,118px)',fontWeight:900,lineHeight:.92,letterSpacing:'-3px',color:'#F2F2F2',textTransform:'uppercase',marginBottom:32}}>
                Votre voiture.<br/>Enfin sous<br/><span style={{color:AC}}>contrôle.</span>
              </h1>

              <div style={{width:44,height:2,background:AC,marginBottom:26}}/>

              <p style={{fontSize:18,color:'#666',lineHeight:1.78,maxWidth:500,marginBottom:44,fontWeight:400}}>
                1PARC met fin à l'opacité de l'entretien automobile. Trouvez des ateliers fiables, comparez les devis, tracez chaque intervention, et payez en Wave — pour chaque véhicule de votre flotte.
              </p>

              <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:56}}>
                <button className="ba" onClick={()=>navigate('/signup')} style={{padding:'14px 28px',borderRadius:10,fontSize:15}}>
                  Créer mon compte gratuit
                </button>
                <button className="bo" onClick={()=>scroll('solution')} style={{padding:'14px 28px',borderRadius:10,fontSize:15}}>
                  Voir comment ça fonctionne
                </button>
              </div>

              <div style={{display:'flex',gap:40,flexWrap:'wrap',borderTop:'1px solid rgba(255,255,255,.06)',paddingTop:32}}>
                {[[fmtCount(stats.carOwners),'Propriétaires inscrits'],[fmtCount(stats.vehicles),'Véhicules suivis'],[fmtCount(stats.workshops),'Ateliers partenaires'],['0','Surprise sur facture']].map(([n,l])=>(
                  <div key={l}>
                    <div className="bc" style={{fontSize:34,fontWeight:900,color:'#F2F2F2',lineHeight:1,marginBottom:4}}>{n}</div>
                    <div style={{fontSize:11,color:'#3A3A3A',fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em'}}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — System readout */}
            <div className="hp" style={{border:'1px solid rgba(255,255,255,.09)',borderRadius:18,overflow:'hidden',background:SU}}>
              {/* Header bar */}
              <div style={{padding:'16px 22px',borderBottom:'1px solid rgba(255,255,255,.06)',display:'flex',justifyContent:'space-between',alignItems:'center',background:'rgba(255,255,255,.02)'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:7,height:7,borderRadius:'50%',background:'#22c55e',boxShadow:'0 0 6px #22c55e'}}/>
                  <span className="mn" style={{fontSize:11,color:'#444'}}>1PARC FLEET MONITOR</span>
                </div>
                <span className="mn" style={{fontSize:10,color:'#2A2A2A'}}>v2.4.1</span>
              </div>
              {/* Vehicle ID */}
              <div style={{padding:'18px 22px',borderBottom:'1px solid rgba(255,255,255,.05)'}}>
                <div style={{fontSize:10,color:'#3A3A3A',marginBottom:4,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase'}}>Véhicule actif</div>
                <div style={{fontSize:20,fontWeight:700,color:'#F2F2F2',marginBottom:2}}>CI-2847-AB</div>
                <div style={{fontSize:12,color:'#444'}}>Toyota Corolla 2021 · 87,420 km</div>
              </div>
              {/* Score + bars */}
              <div style={{padding:'18px 22px',borderBottom:'1px solid rgba(255,255,255,.05)',display:'flex',gap:20,alignItems:'flex-start'}}>
                <div style={{flexShrink:0}}>
                  <div style={{fontSize:10,color:'#3A3A3A',marginBottom:6,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase'}}>Score de flotte</div>
                  <div style={{display:'flex',alignItems:'baseline',gap:3}}>
                    <span className="bc" style={{fontSize:52,fontWeight:900,color:AC,lineHeight:1}}>94</span>
                    <span style={{fontSize:16,color:'#3A3A3A'}}>/100</span>
                  </div>
                  <div style={{fontSize:11,color:AC,fontWeight:700,marginTop:4,letterSpacing:'.04em'}}>● FLEET MASTER</div>
                </div>
                <div style={{flex:1,display:'flex',flexDirection:'column',gap:9,paddingTop:24}}>
                  {[['Assurance',100],['Contrôle Tech.',100],['Vidange',80],['Réactivité',90],['Santé globale',87]].map(([label,pct])=>(
                    <div key={label}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                        <span style={{fontSize:10,color:'#3A3A3A',fontWeight:600}}>{label}</span>
                        <span style={{fontSize:10,color:'#3A3A3A'}}>{pct}%</span>
                      </div>
                      <div className="sb-t">
                        <div className="sb-f" style={{width:`${pct}%`,opacity:pct<85?.55:1}}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Alerts */}
              <div style={{padding:'14px 22px',borderBottom:'1px solid rgba(255,255,255,.05)'}}>
                <div style={{fontSize:10,color:'#3A3A3A',marginBottom:10,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase'}}>Alertes actives</div>
                <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
                  {[['✓ Assurance OK','#22c55e'],['⚠ Vidange 2 400 km','#fbbf24'],['✓ CT Valide','#22c55e']].map(([t,c])=>(
                    <span key={t} style={{fontSize:10,fontWeight:700,padding:'4px 9px',borderRadius:4,background:`${c}14`,color:c,border:`1px solid ${c}22`}}>{t}</span>
                  ))}
                </div>
              </div>
              {/* Last service */}
              <div style={{padding:'16px 22px'}}>
                <div style={{fontSize:10,color:'#3A3A3A',marginBottom:10,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase'}}>Dernière intervention</div>
                <div style={{fontSize:13,fontWeight:700,color:'#F2F2F2',marginBottom:2}}>Vidange + Filtres Air / Huile</div>
                <div style={{fontSize:11,color:'#444',marginBottom:8}}>Garage Auto Pro CI · il y a 3 jours</div>
                <div style={{fontSize:11,color:'#3A3A3A'}}>Huile Total 5W30 · Filtre Bosch OF-940</div>
                <div style={{display:'flex',justifyContent:'space-between',marginTop:12,paddingTop:12,borderTop:'1px solid rgba(255,255,255,.04)'}}>
                  <span style={{fontSize:12,color:'#444'}}>85,000 FCFA</span>
                  <span style={{fontSize:11,fontWeight:700,color:'#22c55e'}}>✓ Payé via Wave</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Scroll cue — invites discovery */}
        <button onClick={()=>scroll('solution')} aria-label="Découvrir la suite" className="hp scroll-cue"
          style={{position:'absolute',bottom:26,left:'50%',transform:'translateX(-50%)',background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:7,color:'#555'}}>
          <span style={{fontSize:10,fontWeight:700,letterSpacing:'.14em',textTransform:'uppercase'}}>Découvrez</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={AC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
      </section>

      {/* ── PAIN ─────────────────────────────────────────────── */}
      <section style={{padding:'120px 0',background:'#040404',borderBottom:'1px solid rgba(255,255,255,.06)'}}>
        <div style={{maxWidth:1280,margin:'0 auto',padding:'0 32px'}}>
          <div ref={rP} className={`rv ${vP?'on':''}`} style={{marginBottom:72}}>
            <div style={{fontSize:11,fontWeight:700,color:'#FF3B30',letterSpacing:'.15em',textTransform:'uppercase',marginBottom:18}}>Le Problème</div>
            <h2 className="bc" style={{fontSize:'clamp(38px,4.5vw,68px)',fontWeight:900,color:'#F2F2F2',textTransform:'uppercase',letterSpacing:'-1.5px',lineHeight:1,maxWidth:640}}>
              Ce que vivent les automobilistes sans 1PARC.
            </h2>
          </div>
          <div className="pg" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:1,background:'rgba(255,255,255,.06)'}}>
            {PAIN.map((p,i)=>(
              <div key={i} className={`rv ${vP?'on':''} d${i+1}`}
                style={{background:'#040404',padding:'44px 36px'}}>
                <div className="bc" style={{fontSize:'clamp(60px,7vw,96px)',fontWeight:900,color:'#FF3B30',lineHeight:1,marginBottom:10,letterSpacing:'-2px'}}>{p.stat}</div>
                <div style={{fontSize:13,fontWeight:700,color:'#F2F2F2',marginBottom:10,textTransform:'uppercase',letterSpacing:'.05em'}}>{p.label}</div>
                <div style={{width:28,height:2,background:'#FF3B30',marginBottom:18}}/>
                <p style={{fontSize:13,color:'#444',lineHeight:1.78}}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTION PIVOT (LIME) ─────────────────────────────── */}
      <section style={{padding:'112px 0',background:AC}}>
        <div style={{maxWidth:1280,margin:'0 auto',padding:'0 32px'}}>
          <div ref={rS} className={`rv ${vS?'on':''}`}>
            <div className="sol-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:80,alignItems:'center'}}>
              <div>
                <h2 className="bc" style={{fontSize:'clamp(48px,6vw,82px)',fontWeight:900,color:'#000',textTransform:'uppercase',letterSpacing:'-2px',lineHeight:.93,marginBottom:28}}>
                  On a construit<br/>la solution.
                </h2>
                <div style={{width:44,height:3,background:'#000',marginBottom:26}}/>
                <p style={{fontSize:17,color:'#222',lineHeight:1.78,marginBottom:36}}>
                  1PARC est la première plateforme qui connecte propriétaires, ateliers et fournisseurs dans un seul écosystème numérique. Transparence des prix. Traçabilité des interventions. Paiement mobile. Intelligence artificielle. Tout en un.
                </p>
                <button className="bd" onClick={()=>navigate('/signup')} style={{padding:'14px 28px',borderRadius:10,fontSize:15}}>
                  Rejoindre 1PARC →
                </button>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:14}}>
                {[
                  ['Ateliers certifiés','Sélectionnés sur leurs vraies performances — notes, délais, qualité des pièces.'],
                  ['Prix transparents','Chaque ligne de facture est justifiée avant que vous payiez. Fini les surprises.'],
                  ['Historique numérique',"L'entretien complet de votre véhicule — enregistré, traçable, permanent."],
                  ['Paiement Wave',"Payez votre mécano instantanément depuis votre téléphone. Il reçoit son argent automatiquement."],
                ].map(([t,d])=>(
                  <div key={t} style={{display:'flex',gap:14,alignItems:'flex-start'}}>
                    <div style={{width:20,height:20,borderRadius:'50%',background:'#000',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:2}}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={AC} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:'#000',marginBottom:2}}>{t}</div>
                      <div style={{fontSize:13,color:'#333',lineHeight:1.6}}>{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────── */}
      {FEATURES.map((f,i)=>{
        const [ref,vis]=featureRefs[i];
        return(
          <section key={i} id={f.id||undefined} style={{padding:'104px 0',background:i%2===0?BG:'#040404',borderBottom:'1px solid rgba(255,255,255,.06)'}}>
            <div style={{maxWidth:1280,margin:'0 auto',padding:'0 32px'}}>
              <div ref={ref} className={`rv ${vis?'on':''}`}>
                <div className="fg" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:80,alignItems:'center'}}>

                  {/* Copy — alternates */}
                  <div style={{order:i%2===0?1:2}}>
                    <div style={{fontSize:11,fontWeight:700,color:AC,letterSpacing:'.14em',textTransform:'uppercase',marginBottom:18}}>{f.tag}</div>
                    <h3 className="bc" style={{fontSize:'clamp(34px,4vw,56px)',fontWeight:900,color:'#F2F2F2',textTransform:'uppercase',letterSpacing:'-1px',lineHeight:1.0,marginBottom:22,whiteSpace:'pre-line'}}>{f.title}</h3>
                    <div style={{width:32,height:2,background:AC,marginBottom:22}}/>
                    <p style={{fontSize:15,color:'#555',lineHeight:1.85,marginBottom:28}}>{f.body}</p>
                    <div style={{display:'flex',flexDirection:'column',gap:9}}>
                      {f.bullets.map(b=>(
                        <div key={b} style={{display:'flex',alignItems:'center',gap:12}}>
                          <div style={{width:4,height:4,borderRadius:'50%',background:AC,flexShrink:0}}/>
                          <span style={{fontSize:13,color:'#555',fontWeight:500}}>{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Visual */}
                  <div className="fv" style={{order:i%2===0?2:1}}>

                    {/* SCORE VISUAL */}
                    {f.visual==='score'&&(
                      <div style={{border:'1px solid rgba(255,255,255,.08)',borderRadius:16,overflow:'hidden',background:SU}}>
                        <div style={{padding:'18px 22px',borderBottom:'1px solid rgba(255,255,255,.06)',display:'flex',justifyContent:'space-between',background:'rgba(255,255,255,.02)'}}>
                          <span className="mn" style={{fontSize:10,color:'#3A3A3A'}}>SCORE DE CONFIANCE ATELIER</span>
                          <span className="mn" style={{fontSize:10,color:AC}}>1PARC CERTIFIED</span>
                        </div>
                        <div style={{padding:'24px 22px'}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:24}}>
                            <div>
                              <div style={{fontSize:11,color:'#3A3A3A',marginBottom:4}}>Garage Auto Pro CI</div>
                              <div className="bc" style={{fontSize:60,fontWeight:900,color:AC,lineHeight:1}}>84</div>
                              <div style={{fontSize:12,color:'#3A3A3A'}}>/100 · Excellent</div>
                            </div>
                            <div style={{textAlign:'right'}}>
                              <div style={{fontSize:10,color:'#2A2A2A',marginBottom:4,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em'}}>Spécialités</div>
                              <div style={{fontSize:12,color:'#444'}}>Toyota · Honda · Kia</div>
                              <div style={{fontSize:12,color:'#3A3A3A'}}>Vidange · Freins · CT</div>
                            </div>
                          </div>
                          {[['Qualité des pièces',90],['Délais de traitement',85],['Satisfaction client',88],['Prix validés',76],['Historique complet',88]].map(([l,v])=>(
                            <div key={l} style={{marginBottom:12}}>
                              <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                                <span style={{fontSize:11,color:'#444',fontWeight:600}}>{l}</span>
                                <span style={{fontSize:11,color:'#3A3A3A'}}>{v}%</span>
                              </div>
                              <div className="sb-t"><div className="sb-f" style={{width:`${v}%`,opacity:v<85?.45:1}}/></div>
                            </div>
                          ))}
                          <div style={{marginTop:18,paddingTop:18,borderTop:'1px solid rgba(255,255,255,.05)',display:'flex',gap:8,flexWrap:'wrap'}}>
                            {['847 interventions','4.8★ / 5','∅ 2 jours de délai'].map(tag=>(
                              <span key={tag} style={{fontSize:10,color:'#3A3A3A',background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)',padding:'4px 10px',borderRadius:4}}>{tag}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* FLOW VISUAL */}
                    {f.visual==='flow'&&(
                      <div style={{display:'flex',flexDirection:'column',gap:6}}>
                        {[
                          {s:'done',label:'Demande de service',desc:"Sélectionnez votre véhicule et le type d'intervention"},
                          {s:'done',label:'Devis reçus',desc:'3 ateliers partenaires ont répondu sous 2h'},
                          {s:'done',label:'Devis accepté',desc:'Garage Pro CI — 85,000 FCFA · Détaillé ligne par ligne'},
                          {s:'done',label:'Facture émise',desc:'Vidange + Filtres + TVA + Marques des pièces'},
                          {s:'active',label:'Paiement Wave',desc:'Depuis votre téléphone en moins de 10 secondes'},
                          {s:'pending',label:'Virement automatique',desc:"L'atelier reçoit 90% du montant directement sur Wave"},
                        ].map((step,j)=>(
                          <div key={j} style={{display:'flex',gap:14,padding:'14px 18px',background:step.s==='active'?`rgba(180,255,58,.04)`:SU,border:`1px solid ${step.s==='active'?'rgba(180,255,58,.14)':'rgba(255,255,255,.055)'}`,borderRadius:10}}>
                            <div className="mn" style={{fontSize:13,color:step.s==='done'?AC:step.s==='active'?AC:'#2A2A2A',minWidth:16,marginTop:2,fontWeight:700,flexShrink:0}}>
                              {step.s==='done'?'✓':step.s==='active'?'▶':'·'}
                            </div>
                            <div>
                              <div style={{fontSize:13,fontWeight:700,color:step.s==='pending'?'#2A2A2A':'#F2F2F2',marginBottom:2}}>{step.label}</div>
                              <div style={{fontSize:12,color:'#3A3A3A'}}>{step.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* AI VISUAL */}
                    {f.visual==='ai'&&(
                      <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        <div style={{border:'1px solid rgba(255,255,255,.07)',borderRadius:14,overflow:'hidden',background:SU}}>
                          <div style={{padding:'13px 18px',borderBottom:'1px solid rgba(255,255,255,.06)',background:'rgba(255,255,255,.02)'}}>
                            <span className="mn" style={{fontSize:10,color:AC,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em'}}>● DIAGNOSTIC IA — 1PARC</span>
                          </div>
                          <div style={{padding:'14px 18px',borderBottom:'1px solid rgba(255,255,255,.05)',background:'rgba(255,255,255,.015)'}}>
                            <div style={{fontSize:10,color:'#3A3A3A',marginBottom:5}}>Symptôme décrit par le propriétaire</div>
                            <div style={{fontSize:13,color:'#F2F2F2',fontWeight:500,fontStyle:'italic'}}>"Vibrations au freinage à haute vitesse"</div>
                          </div>
                          <div style={{padding:'14px 18px'}}>
                            <div style={{fontSize:10,color:'#3A3A3A',marginBottom:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em'}}>Analyse</div>
                            {[{cause:'Disques de frein voilés',prob:'Très probable',urg:'HAUTE',c:'#FF3B30'},{cause:'Plaquettes usées',prob:'Probable',urg:'MOYENNE',c:'#f59e0b'}].map(c=>(
                              <div key={c.cause} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                                <div>
                                  <div style={{fontSize:13,fontWeight:600,color:'#F2F2F2'}}>{c.cause}</div>
                                  <div style={{fontSize:11,color:'#3A3A3A'}}>{c.prob}</div>
                                </div>
                                <span style={{fontSize:10,fontWeight:800,color:c.c,background:`${c.c}18`,padding:'3px 8px',borderRadius:4,letterSpacing:'.05em'}}>{c.urg}</span>
                              </div>
                            ))}
                            <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid rgba(255,255,255,.05)',display:'flex',justifyContent:'space-between'}}>
                              <span style={{fontSize:11,color:'#3A3A3A'}}>Coût estimé</span>
                              <span style={{fontSize:13,fontWeight:700,color:'#F2F2F2'}}>45 000 – 80 000 FCFA</span>
                            </div>
                          </div>
                        </div>
                        <div style={{border:'1px solid rgba(251,191,36,.14)',borderRadius:14,padding:'14px 18px',background:'rgba(251,191,36,.03)'}}>
                          <div style={{fontSize:10,color:'#f59e0b',fontWeight:700,marginBottom:8,textTransform:'uppercase',letterSpacing:'.1em'}}>⚠ Prédiction IA — Prévention</div>
                          <div style={{fontSize:13,fontWeight:700,color:'#F2F2F2',marginBottom:4}}>Courroie de distribution</div>
                          <div style={{fontSize:12,color:'#444',lineHeight:1.65}}>Remplacement prévu dans <strong style={{color:'#f59e0b'}}>3–4 mois</strong> selon l'historique. Coût estimé : 120 000 – 180 000 FCFA.</div>
                        </div>
                        <div style={{border:'1px solid rgba(255,255,255,.07)',borderRadius:14,padding:'14px 18px',background:SU}}>
                          <div style={{fontSize:10,color:AC,fontWeight:700,marginBottom:8,textTransform:'uppercase',letterSpacing:'.1em'}}>● Pièces suggérées — IA ObservedFitment</div>
                          <div style={{fontSize:11,color:'#3A3A3A',marginBottom:10}}>Toyota Corolla 2021 · basé sur 847 interventions similaires</div>
                          {[{b:'Total Quartz 5W30',r:'TOT-5W30-5L',p:'8 500 – 12 000 FCFA',n:312},{b:'Bosch Oil Filter',r:'OF-940',p:'3 200 – 4 800 FCFA',n:289}].map(p=>(
                            <div key={p.r} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid rgba(255,255,255,.04)'}}>
                              <div>
                                <div style={{fontSize:12,fontWeight:600,color:'#F2F2F2'}}>{p.b}</div>
                                <div style={{fontSize:10,color:'#2A2A2A'}}>{p.r} · {p.n}× utilisé</div>
                              </div>
                              <div style={{fontSize:12,color:AC,fontWeight:600}}>{p.p}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* WORKSHOP VISUAL */}
                    {f.visual==='workshop'&&(
                      <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8}}>
                          {[['12','Demandes actives',AC],['8','Devis en attente','#F2F2F2'],['3','Factures impayées','#f59e0b'],['1.2M FCFA','Encaissé ce mois','#F2F2F2']].map(([v,l,c])=>(
                            <div key={l} style={{border:'1px solid rgba(255,255,255,.07)',borderRadius:12,padding:'18px 16px',background:SU}}>
                              <div className="bc" style={{fontSize:30,fontWeight:900,color:c,lineHeight:1,marginBottom:4}}>{v}</div>
                              <div style={{fontSize:11,color:'#3A3A3A',fontWeight:600}}>{l}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{border:'1px solid rgba(255,255,255,.07)',borderRadius:12,padding:'18px',background:SU}}>
                          <div style={{fontSize:10,color:'#3A3A3A',marginBottom:10,textTransform:'uppercase',fontWeight:700,letterSpacing:'.1em'}}>Partage WhatsApp instantané</div>
                          <div style={{fontSize:13,fontWeight:600,color:'#F2F2F2',marginBottom:4}}>Facture #INV-0091 · Mbaye Koné</div>
                          <div style={{fontSize:11,color:'#3A3A3A',marginBottom:14}}>85 000 FCFA · Toyota Corolla CI-2847-AB</div>
                          <div style={{display:'flex',gap:8}}>
                            <div style={{flex:1,height:34,background:'rgba(255,255,255,.04)',borderRadius:8,border:'1px solid rgba(255,255,255,.06)'}}/>
                            <div style={{padding:'8px 16px',background:'#25D366',borderRadius:8,fontSize:12,fontWeight:700,color:'#fff',whiteSpace:'nowrap'}}>Envoyer →</div>
                          </div>
                        </div>
                        <div style={{border:`1px solid rgba(180,255,58,.12)`,borderRadius:12,padding:'16px 18px',background:'rgba(180,255,58,.02)'}}>
                          <div style={{fontSize:10,color:AC,fontWeight:700,marginBottom:8,textTransform:'uppercase',letterSpacing:'.1em'}}>✓ Virement Wave automatique reçu</div>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                            <div>
                              <div style={{fontSize:18,fontWeight:700,color:'#F2F2F2'}}>76 500 FCFA</div>
                              <div style={{fontSize:11,color:'#3A3A3A'}}>Virement reçu · Via Wave</div>
                            </div>
                            <div style={{width:38,height:38,borderRadius:10,background:'rgba(180,255,58,.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>💳</div>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── ECOSYSTEM ────────────────────────────────────────── */}
      <section id="ecosysteme" style={{padding:'120px 0',background:BG,borderTop:'1px solid rgba(255,255,255,.06)'}}>
        <div style={{maxWidth:1280,margin:'0 auto',padding:'0 32px'}}>
          <div ref={rE} className={`rv ${vE?'on':''}`} style={{marginBottom:64}}>
            <div style={{fontSize:11,fontWeight:700,color:AC,letterSpacing:'.15em',textTransform:'uppercase',marginBottom:18}}>L'Écosystème</div>
            <h2 className="bc" style={{fontSize:'clamp(38px,4.5vw,68px)',fontWeight:900,color:'#F2F2F2',textTransform:'uppercase',letterSpacing:'-1.5px',lineHeight:1,maxWidth:760}}>
              Une plateforme.<br/>Quatre acteurs. Un seul objectif.
            </h2>
          </div>
          <div className="eg" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:1,background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.06)'}}>
            {[
              {icon:'🚗',role:'Propriétaires',color:AC,pts:['Score de flotte en temps réel','Alertes assurance & CT','Devis comparatifs','Paiement Wave','Historique complet']},
              {icon:'🔧',role:'Ateliers',color:'#60a5fa',pts:['Demandes en temps réel','Devis & factures pro','Partage WhatsApp','Virement Wave auto','Analytics de performance']},
              {icon:'📦',role:'Fournisseurs',color:'#a78bfa',pts:['Bons de commande digitaux','Catalogue tracé','Factures B2B','Paiement sécurisé','Réseau équitable']},
              {icon:'🛡️',role:'Assureurs',color:'#34d399',pts:['Données véhicules fiables','Historique entretien','Score de conformité','Preuves numériques','Rapports automatisés']},
            ].map((e,i)=>(
              <div key={i} className={`hc rv ${vE?'on':''} d${i+1}`}
                style={{background:BG,padding:'40px 28px',border:'1px solid transparent',cursor:'default'}}>
                <div style={{fontSize:30,marginBottom:16}}>{e.icon}</div>
                <div className="bc" style={{fontSize:22,fontWeight:900,color:'#F2F2F2',textTransform:'uppercase',marginBottom:8}}>{e.role}</div>
                <div style={{width:22,height:2,background:e.color,marginBottom:18}}/>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {e.pts.map(p=>(
                    <div key={p} style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:4,height:4,borderRadius:'50%',background:e.color,flexShrink:0}}/>
                      <span style={{fontSize:12,color:'#444'}}>{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAR PAINT ────────────────────────────────────────── */}
      <CarPaintSimulator/>

      {/* ── NOTRE HISTOIRE (emotional) ───────────────────────── */}
      <section id="histoire" style={{padding:'130px 0',background:'#040404',borderTop:'1px solid rgba(255,255,255,.06)',borderBottom:'1px solid rgba(255,255,255,.06)',position:'relative'}}>
        {/* faint radial glow to make the section feel warmer */}
        <div style={{position:'absolute',top:'-10%',left:'50%',transform:'translateX(-50%)',width:680,height:680,borderRadius:'50%',background:`radial-gradient(circle, rgba(180,255,58,.05) 0%, transparent 62%)`,pointerEvents:'none'}}/>
        <div ref={rA} className={`rv ${vA?'on':''}`} style={{maxWidth:760,margin:'0 auto',padding:'0 32px',position:'relative'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,justifyContent:'center',marginBottom:26}}>
            <div style={{width:7,height:7,borderRadius:'50%',background:AC,boxShadow:`0 0 8px ${AC}`}}/>
            <span style={{fontSize:11,fontWeight:700,color:AC,letterSpacing:'.16em',textTransform:'uppercase'}}>Notre Histoire</span>
          </div>

          <h2 className="bc" style={{fontSize:'clamp(40px,5.5vw,76px)',fontWeight:900,color:'#F2F2F2',textTransform:'uppercase',letterSpacing:'-1.5px',lineHeight:.98,textAlign:'center',marginBottom:40}}>
            Pourquoi 1PARC<br/><span style={{color:AC}}>existe.</span>
          </h2>

          <div style={{display:'flex',flexDirection:'column',gap:24,fontSize:'clamp(16px,1.6vw,19px)',lineHeight:1.85,color:'#9A9A9A',fontWeight:400}}>
            <p>
              Vous entrez dans un garage avec un problème précis. Quelques heures plus tard, on vous tend un devis rempli de termes et de références que vous ne connaissez pas, et on vous demande des centaines de milliers de francs CFA sans que vous sachiez vraiment ce que vous achetez.
            </p>
            <p>
              Pendant plus de <strong style={{color:'#F2F2F2',fontWeight:600}}>12 ans</strong> dans l'automobile à Abidjan, j'ai vu cette scène se répéter mille fois. Pour comprendre, j'ai échangé en face à face avec <strong style={{color:'#F2F2F2',fontWeight:600}}>plus de 1 000 propriétaires</strong>. Une conclusion est devenue évidente.
            </p>
          </div>

          {/* Pull quote */}
          <div style={{margin:'48px 0',paddingLeft:26,borderLeft:`3px solid ${AC}`}}>
            <p className="bc" style={{fontSize:'clamp(26px,3.4vw,40px)',fontWeight:800,color:'#F2F2F2',lineHeight:1.12,letterSpacing:'-.5px'}}>
              « Le véritable problème n'est pas la mécanique. C'est le manque de transparence. »
            </p>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:24,fontSize:'clamp(16px,1.6vw,19px)',lineHeight:1.85,color:'#9A9A9A',fontWeight:400}}>
            <p>
              1PARC est né de cette conviction&nbsp;: la confiance ne doit jamais être une supposition. <strong style={{color:'#F2F2F2',fontWeight:600}}>Elle doit être démontrée</strong> — un véhicule, un atelier, un paiement transparent à la fois.
            </p>
          </div>

          {/* Signature + link to full story */}
          <div style={{marginTop:44,paddingTop:28,borderTop:'1px solid rgba(255,255,255,.07)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:20,flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:42,height:42,borderRadius:'50%',background:'rgba(180,255,58,.1)',border:`1px solid rgba(180,255,58,.2)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Logo style={{height:18}}/>
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'#F2F2F2'}}>Wael El Hajj</div>
                <div style={{fontSize:12,color:'#555'}}>Fondateur de 1PARC · Abidjan, Côte d'Ivoire</div>
              </div>
            </div>
            <button className="bo" onClick={()=>navigate('/about')} style={{padding:'12px 24px',borderRadius:10,fontSize:14}}>
              Lire notre histoire complète →
            </button>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section style={{background:AC}}>
        <div className="cta-wrap" style={{maxWidth:1280,margin:'0 auto',padding:'100px 32px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:40,flexWrap:'wrap'}}>
          <div>
            <h2 className="bc" style={{fontSize:'clamp(48px,7vw,96px)',fontWeight:900,color:'#000',textTransform:'uppercase',letterSpacing:'-2.5px',lineHeight:.9,marginBottom:20}}>
              Reprenez<br/>le contrôle.
            </h2>
            <p style={{fontSize:17,color:'#333',maxWidth:400,lineHeight:1.7}}>
              Créez votre compte. Enregistrez vos véhicules. Ne vous faites plus jamais avoir.
            </p>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:12,minWidth:280}}>
            <button className="bd" onClick={()=>navigate('/signup')} style={{padding:'18px 36px',borderRadius:12,fontSize:16,textAlign:'center',letterSpacing:'.02em'}}>
              Je crée mon compte →
            </button>
            <button onClick={()=>navigate('/login')} style={{padding:'13px 36px',borderRadius:12,fontSize:14,background:'transparent',border:'1px solid rgba(0,0,0,.18)',color:'#333',cursor:'pointer',fontFamily:"'Manrope',sans-serif",fontWeight:600,transition:'all .2s'}}>
              J'ai déjà un compte
            </button>
            <p style={{fontSize:12,color:'#666',textAlign:'center'}}>Aucune carte de crédit requise</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer style={{padding:'60px 0 28px',background:'#030303',borderTop:'1px solid rgba(255,255,255,.04)'}}>
        <div style={{maxWidth:1280,margin:'0 auto',padding:'0 32px'}}>
          <div className="ftg" style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:48,marginBottom:48}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18}}>
                <Logo style={{height:24}}/>
                <span className="bc" style={{fontSize:18,fontWeight:900,color:'#F2F2F2',textTransform:'uppercase'}}>1PARC</span>
              </div>
              <p style={{fontSize:13,color:'#2A2A2A',lineHeight:1.8,maxWidth:300}}>
                La première plateforme de gestion de flotte et d'entretien automobile transparente en Côte d'Ivoire.
              </p>
              <div style={{marginTop:16}}>
                <span style={{fontSize:11,color:AC,fontWeight:700,letterSpacing:'.06em'}}>CLARTÉ. CONFIANCE. CONTRÔLE.</span>
              </div>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:'#2A2A2A',letterSpacing:'.15em',textTransform:'uppercase',marginBottom:18}}>Navigation</div>
              {[['solution','La Solution',false],[null,'Connexion',true,'/login'],[null,'Inscription',true,'/signup']].map(([id,label,isRoute,path],j)=>(
                <div key={j} style={{marginBottom:10}}>
                  <button onClick={()=>isRoute?navigate(path):scroll(id)}
                    style={{background:'none',border:'none',color:'#2A2A2A',cursor:'pointer',fontSize:13,fontFamily:"'Manrope',sans-serif",fontWeight:500,padding:0,transition:'color .2s'}}
                    onMouseEnter={e=>e.target.style.color='#F2F2F2'} onMouseLeave={e=>e.target.style.color='#2A2A2A'}>
                    {label}
                  </button>
                </div>
              ))}
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:'#2A2A2A',letterSpacing:'.15em',textTransform:'uppercase',marginBottom:18}}>Légal</div>
              {[['Confidentialité','/confidentialite'],['Conditions Générales','/conditions']].map(([l,path])=>(
                <div key={l} style={{marginBottom:10}}>
                  <button onClick={()=>navigate(path)}
                    style={{background:'none',border:'none',padding:0,cursor:'pointer',fontFamily:"'Manrope',sans-serif",color:'#2A2A2A',fontSize:13,textDecoration:'none',transition:'color .2s'}}
                    onMouseEnter={e=>e.target.style.color='#F2F2F2'} onMouseLeave={e=>e.target.style.color='#2A2A2A'}>{l}</button>
                </div>
              ))}
            </div>
          </div>
          <div style={{borderTop:'1px solid rgba(255,255,255,.04)',paddingTop:22,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12}}>
            <span style={{fontSize:11,color:'#1A1A1A',fontWeight:700,letterSpacing:'.05em'}}>© {new Date().getFullYear()} 1PARC. TOUS DROITS RÉSERVÉS.</span>
            <div style={{display:'flex',gap:20}}>
              {['LinkedIn','Twitter'].map(s=>(
                <span key={s} style={{fontSize:11,color:'#1A1A1A',cursor:'pointer',fontWeight:700,transition:'color .2s'}}
                  onMouseEnter={e=>e.target.style.color=AC} onMouseLeave={e=>e.target.style.color='#1A1A1A'}>{s}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
